import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, MessageCircle, Calendar, Award, Stethoscope, ChevronLeft, ChevronRight, Star, Quote, MapPin, Mail } from "lucide-react";
import { DOCTOR, TREATMENTS, TEAM_PROFILES, FACILITIES, INSURANCE_PARTNERS, FALLBACK_TESTIMONIALS, CONTACT_NUMBERS } from "@/data/static";
import Counter from "@/components/Counter";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import MeetTheTeam from "@/components/MeetTheTeam";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import api from "@/lib/api";

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } };

function Hero() {
  return (
    <section className="relative pt-32 lg:pt-40 pb-20 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-sky-100 -z-10" />
      <div className="absolute -top-20 -right-20 w-[480px] h-[480px] rounded-full bg-primary/10 blur-3xl -z-10" />
      <div className="absolute -bottom-20 -left-20 w-[420px] h-[420px] rounded-full bg-teal/10 blur-3xl -z-10" />
      <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="overline">Bone &amp; Joint Specialist · Sasaram, Bihar</span>
          <h1 className="font-heading mt-4 text-5xl sm:text-6xl lg:text-7xl tracking-tight font-light text-slate-900 leading-[1.05]">
            Restoring <em className="italic font-medium text-primary">movement</em>, <br/>
            rebuilding lives.
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed font-body">
            {DOCTOR.name} — {DOCTOR.title}. <br/>
            {DOCTOR.qualifications} · {DOCTOR.experience} of clinical excellence at {DOCTOR.hospital}.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/appointment" className="btn-primary" data-testid="hero-book-btn">
              <Calendar className="w-4 h-4" /> Book Appointment <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`https://wa.me/${DOCTOR.whatsapp}`} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="hero-whatsapp-btn">
              <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp
            </a>
            <a href={`tel:${DOCTOR.phoneRaw}`} className="btn-secondary" data-testid="hero-call-btn">
              <Phone className="w-4 h-4 text-primary" /> Call Now
            </a>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
            {[{n:20,s:"+",l:"Years Exp."},{n:10000,s:"+",l:"Surgeries"},{n:98,s:"%",l:"Success"}].map((s,i)=>(
              <div key={i} className="">
                <div className="text-3xl font-heading font-semibold text-primary"><Counter end={s.n} suffix={s.s}/></div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }} className="relative">
          <div className="relative aspect-[4/5] rounded-[32px] overflow-hidden bg-gradient-to-br from-primary/20 to-teal/20 shadow-medium">
            <img src={DOCTOR.photo} alt={DOCTOR.name} className="w-full h-full object-cover" />
          </div>
          <div className="absolute -left-6 bottom-12 glass rounded-2xl p-4 shadow-soft animate-float">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center"><Award className="w-5 h-5 text-primary"/></div>
              <div>
                <div className="font-heading font-semibold text-slate-900">Trusted Care</div>
                <div className="text-xs text-slate-500">Sasaram · Bihar</div>
              </div>
            </div>
          </div>
          <div className="absolute -right-4 top-12 glass rounded-2xl p-4 shadow-soft animate-float" style={{animationDelay:"1s"}}>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-teal/10 flex items-center justify-center"><Stethoscope className="w-5 h-5 text-teal"/></div>
              <div>
                <div className="font-heading font-semibold text-slate-900">Top Hospital</div>
                <div className="text-xs text-slate-500">{DOCTOR.hospital}</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function DoctorProfile({ p }) {
  return (
    <section className="section" data-testid={`doctor-profile-${p.id}`}>
      <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
        <motion.div {...fadeUp} className="relative">
          <div className="rounded-3xl overflow-hidden shadow-medium">
            <img src={p.photo} alt={p.name} className="w-full h-[520px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -right-6 bg-primary text-white rounded-2xl p-6 max-w-xs shadow-medium">
            <div className="text-5xl font-heading font-light">{p.stat}{p.statSup && <sup className="text-2xl">{p.statSup}</sup>}</div>
            <div className="text-sm mt-2 opacity-90">{p.statLabel}</div>
          </div>
        </motion.div>
        <motion.div {...fadeUp}>
          <span className="overline">{p.overline}</span>
          <h2 className="font-heading mt-4 text-4xl sm:text-5xl tracking-tight font-medium text-slate-900">{p.headlinePre}<em className="italic font-light text-primary">{p.headlineEm}</em>{p.headlinePost}</h2>
          <p className="mt-5 text-slate-600 leading-relaxed">{p.bio}</p>
          <div className="mt-8 grid grid-cols-2 gap-5">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400">Qualifications</div>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {p.degrees.map((d,i)=><li key={i} className="flex gap-2"><span className="text-primary">·</span>{d}</li>)}
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400">Memberships</div>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {p.memberships.map((d,i)=><li key={i} className="flex gap-2"><span className="text-primary">·</span>{d}</li>)}
              </ul>
            </div>
          </div>
          <div className="mt-7 flex flex-wrap gap-2">
            {p.languages.map(l => (
              <span key={l} className="text-xs px-3 py-1.5 rounded-full bg-primary/5 text-primary border border-primary/10">{l}</span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Treatments() {
  return (
    <section id="treatments" className="section bg-white" data-testid="treatments-section">
      <div className="container-x">
        <motion.div {...fadeUp} className="max-w-2xl">
          <span className="overline">Treatments & Surgeries</span>
          <h2 className="font-heading mt-4 text-4xl sm:text-5xl tracking-tight font-medium">Specialised care for every bone & joint.</h2>
        </motion.div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {TREATMENTS.map((t,i)=>{
            const Icon = t.icon;
            return (
              <motion.div
                key={t.slug}
                initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-60px"}}
                transition={{duration:0.5,delay:i*0.05}}
                className="group bg-white rounded-2xl border border-slate-100 p-6 shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all"
                data-testid={`treatment-card-${t.slug}`}
              >
                <div className="h-12 w-12 rounded-xl bg-primary/8 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                  <Icon className="w-6 h-6" strokeWidth={1.5}/>
                </div>
                <div className="mt-5 font-heading text-lg font-medium text-slate-900">{t.title}</div>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{t.desc}</p>
                <Link to="/treatments" className="mt-4 inline-flex items-center gap-1 text-sm text-primary font-medium group-hover:gap-2 transition-all">
                  Learn more <ArrowRight className="w-4 h-4"/>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BeforeAfter() {
  const [cases, setCases] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(()=>{
    api.get("/before-after").then(({data})=>{ if(data?.length) setCases(data); }).catch(()=>{});
  },[]);

  const c = cases[idx];
  const next = () => setIdx((idx+1)%cases.length);
  const prev = () => setIdx((idx-1+cases.length)%cases.length);

  if(!c) return null;

  return (
    <section className="section bg-gradient-to-br from-[#0A1B2E] to-[#0F4C81] text-white relative overflow-hidden" data-testid="before-after-section">
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_20%_30%,white,transparent_40%)]"/>
      <div className="container-x relative">
        <motion.div {...fadeUp} className="max-w-2xl">
          <span className="overline !text-sky-300">Real Patient Results</span>
          <h2 className="font-heading mt-4 text-4xl sm:text-5xl tracking-tight font-medium">Before vs After <em className="italic font-light">Surgery</em>.</h2>
          <p className="mt-4 text-slate-300">Drag the slider to compare patient outcomes from real cases performed by Dr. Kumar.</p>
        </motion.div>
        <div className="mt-12 grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3">
            <BeforeAfterSlider before={c.before_url} after={c.after_url} />
          </div>
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-white/5 backdrop-blur p-7 border border-white/10">
              <div className="text-xs uppercase tracking-wider text-sky-300">Case #{idx+1}</div>
              <div className="mt-2 font-heading text-2xl">{c.name || "Patient"} {c.age?`· ${c.age} yrs`:""}</div>
              <dl className="mt-6 space-y-4 text-sm">
                <div><dt className="text-slate-400 text-xs uppercase tracking-wider">Problem</dt><dd className="mt-1">{c.problem}</dd></div>
                <div><dt className="text-slate-400 text-xs uppercase tracking-wider">Treatment</dt><dd className="mt-1">{c.treatment}</dd></div>
                <div><dt className="text-slate-400 text-xs uppercase tracking-wider">Recovery</dt><dd className="mt-1">{c.recovery_time}</dd></div>
                <div><dt className="text-slate-400 text-xs uppercase tracking-wider">Result</dt><dd className="mt-1 text-teal-200">{c.result}</dd></div>
              </dl>
              <div className="mt-7 flex items-center gap-3">
                <button onClick={prev} aria-label="Previous" className="h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="ba-prev"><ChevronLeft className="w-5 h-5"/></button>
                <button onClick={next} aria-label="Next" className="h-11 w-11 rounded-full bg-white text-primary hover:bg-sky-100 flex items-center justify-center" data-testid="ba-next"><ChevronRight className="w-5 h-5"/></button>
                <div className="ml-auto text-xs text-slate-400">{idx+1} / {cases.length}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const [items, setItems] = useState(FALLBACK_TESTIMONIALS);
  useEffect(()=>{ api.get("/testimonials").then(({data})=>{ if(data?.length) setItems(data); }).catch(()=>{}); },[]);
  return (
    <section className="section" data-testid="testimonials-section">
      <div className="container-x">
        <motion.div {...fadeUp} className="max-w-2xl">
          <span className="overline">Patient Success Stories</span>
          <h2 className="font-heading mt-4 text-4xl sm:text-5xl tracking-tight font-medium">Voices of recovery.</h2>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.slice(0,6).map((t,i)=>(
            <motion.div key={t.id} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.5,delay:i*0.08}}
              className="bg-white rounded-2xl p-7 border border-slate-100 shadow-soft hover:shadow-medium transition-shadow" data-testid={`testimonial-${i}`}>
              <Quote className="w-7 h-7 text-primary/30"/>
              <p className="mt-3 text-slate-700 leading-relaxed">"{t.story}"</p>
              <div className="flex gap-0.5 mt-4">{Array.from({length:t.rating||5}).map((_,j)=><Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400"/>)}</div>
              <div className="mt-5 flex items-center gap-3">
                {t.photo_url ? <img src={t.photo_url} alt="" className="h-11 w-11 rounded-full object-cover"/> : <div className="h-11 w-11 rounded-full bg-primary/10 text-primary font-heading font-semibold flex items-center justify-center">{t.name?.[0]}</div>}
                <div>
                  <div className="font-medium text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.surgery} · {t.visit_date}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Facilities() {
  return (
    <section className="section" data-testid="facilities-section">
      <div className="container-x">
        <motion.div {...fadeUp} className="max-w-2xl">
          <span className="overline">Hospital Facilities</span>
          <h2 className="font-heading mt-4 text-4xl sm:text-5xl tracking-tight font-medium">A complete care ecosystem.</h2>
        </motion.div>
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
          {FACILITIES.map((f,i)=>{
            const Icon = f.icon;
            return (
              <motion.div key={i} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.4,delay:i*0.03}}
                className="bg-white rounded-2xl border border-slate-100 p-5 text-center shadow-soft hover:border-primary/30 transition-all">
                <Icon className="w-7 h-7 text-primary mx-auto" strokeWidth={1.5}/>
                <div className="mt-3 text-sm font-medium text-slate-700">{f.label}</div>
              </motion.div>
            );
          })}
        </div>
        <div className="mt-16">
          <div className="text-xs uppercase tracking-widest text-slate-400 text-center">Insurance Partners</div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 lg:gap-5">
            {INSURANCE_PARTNERS.map(p=>(
              <div key={p} className="px-5 py-3 rounded-xl border border-slate-200 bg-white text-slate-600 font-medium text-sm shadow-soft">{p}</div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQs() {
  const [items, setItems] = useState([]);
  useEffect(()=>{ api.get("/faqs").then(({data})=>setItems(data||[])).catch(()=>{}); },[]);
  return (
    <section className="section bg-white" data-testid="faqs-section">
      <div className="container-x grid lg:grid-cols-2 gap-14">
        <motion.div {...fadeUp}>
          <span className="overline">FAQs</span>
          <h2 className="font-heading mt-4 text-4xl sm:text-5xl tracking-tight font-medium">Answers to common questions.</h2>
          <p className="mt-5 text-slate-600">Don&apos;t see your question? Speak directly with our team at Aastha.</p>
          <Link to="/contact" className="btn-primary mt-7">Contact Us <ArrowRight className="w-4 h-4"/></Link>
        </motion.div>
        <motion.div {...fadeUp}>
          <Accordion type="single" collapsible className="space-y-3">
            {items.map((f,i)=>(
              <AccordionItem key={f.id} value={f.id} className="bg-slate-50 rounded-2xl border-none px-5" data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-heading text-base hover:no-underline">{f.question}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}

function EmergencyCTA() {
  return (
    <section className="section" data-testid="emergency-section">
      <div className="container-x">
        <div className="rounded-3xl bg-gradient-to-r from-primary to-primary-light text-white p-10 lg:p-14 grid lg:grid-cols-2 gap-10 items-center shadow-medium relative overflow-hidden">
          <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-white/10"/>
          <div>
            <span className="overline !text-sky-200">Emergency · Available 24×7</span>
            <h3 className="font-heading text-3xl sm:text-4xl font-medium mt-3">For orthopedic emergencies, every minute matters.</h3>
            <p className="mt-4 text-sky-100">Direct line to Dr. Kamal Kishore's trauma response team at Aastha Maternity & Ortho Care Centre, Sasaram.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-2 text-sm">
              {CONTACT_NUMBERS.map(n => (
                <a key={n.raw} href={`tel:${n.raw}`} className="flex items-center gap-2 text-sky-100 hover:text-white"><Phone className="w-4 h-4"/> <span className="opacity-70">{n.label}:</span> <span className="font-medium">{n.number}</span></a>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={`tel:${DOCTOR.phoneRaw}`} className="inline-flex items-center gap-2 bg-white text-primary rounded-full px-7 py-4 font-medium hover:bg-sky-50"><Phone className="w-4 h-4"/> Emergency Call</a>
            <a href={`https://wa.me/${DOCTOR.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-7 py-4 font-medium hover:bg-[#1ebe57]"><MessageCircle className="w-4 h-4"/> WhatsApp</a>
            <a href={`mailto:${DOCTOR.email}`} className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-white rounded-full px-7 py-4 font-medium hover:bg-white/20"><Mail className="w-4 h-4"/> Email</a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero/>
      {TEAM_PROFILES.map(p => <DoctorProfile key={p.id} p={p} />)}
      <MeetTheTeam/>
      <Treatments/>
      <BeforeAfter/>
      <Testimonials/>
      <Facilities/>
      <FAQs/>
      <EmergencyCTA/>
    </main>
  );
}
